import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiRequest, API_ENDPOINTS } from "../utils/api";
import {
    filterFinanceRecords,
    computeRecordsSummary,
} from "../utils/financialTransforms";
import SearchableApartmentSelect from "./SearchableApartmentSelect";
import FinancialSummaryCards from "./FinancialSummaryCards";
import FinancialTransactionsTable from "./FinancialTransactionsTable";

const MONTHS = [
    { value: "", label: "All months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];

export default function ApartmentFinancialModal({ open, onClose, apartments: apartmentsProp = [] }) {
    const [selectedApartmentId, setSelectedApartmentId] = useState(null);
    const [records, setRecords] = useState([]);
    const [apartmentNumber, setApartmentNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [apartmentsLoading, setApartmentsLoading] = useState(false);
    const [apartmentList, setApartmentList] = useState([]);
    const [error, setError] = useState("");

    const [yearFilter, setYearFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");
    const [retryKey, setRetryKey] = useState(0);

    const resetState = useCallback(() => {
        setSelectedApartmentId(null);
        setRecords([]);
        setApartmentNumber("");
        setLoading(false);
        setError("");
        setYearFilter("");
        setMonthFilter("");
        setRetryKey(0);
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onClose();
    }, [onClose, resetState]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") handleClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, handleClose]);

    useEffect(() => {
        if (!open) return;

        if (apartmentsProp.length > 0) {
            setApartmentList(apartmentsProp);
            setApartmentsLoading(false);
            return;
        }

        let cancelled = false;
        setApartmentsLoading(true);

        (async () => {
            try {
                const data = await apiRequest(API_ENDPOINTS.APARTMENTS);
                if (!cancelled) {
                    setApartmentList(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error("Error loading apartments for financial modal:", err);
                if (!cancelled) setApartmentList([]);
            } finally {
                if (!cancelled) setApartmentsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, apartmentsProp]);

    useEffect(() => {
        if (!selectedApartmentId) {
            setRecords([]);
            setApartmentNumber("");
            setError("");
            return;
        }

        let cancelled = false;

        const fetchFinancials = async () => {
            setLoading(true);
            setError("");
            setRecords([]);

            try {
                const data = await apiRequest(
                    API_ENDPOINTS.APARTMENT_FINANCIALS(selectedApartmentId)
                );
                if (cancelled) return;

                setRecords(Array.isArray(data.records) ? data.records : []);
                setApartmentNumber(data.apartment_number || "");
            } catch (err) {
                if (cancelled) return;
                console.error("Error loading apartment financials:", err);
                setError("Unable to load financial records. Please try again.");
                setRecords([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchFinancials();
        return () => {
            cancelled = true;
        };
    }, [selectedApartmentId, retryKey]);

    const filteredRecords = useMemo(
        () =>
            filterFinanceRecords(records, {
                year: yearFilter,
                month: monthFilter,
            }),
        [records, yearFilter, monthFilter]
    );

    const summary = useMemo(() => {
        if (!selectedApartmentId || loading || error) return null;
        return computeRecordsSummary(filteredRecords);
    }, [selectedApartmentId, loading, error, filteredRecords]);

    const availableYears = useMemo(() => {
        const years = new Set();
        records.forEach((record) => {
            const date = record.check_in_date || record.created_at;
            if (date) years.add(new Date(date).getFullYear());
        });
        return [...years].sort((a, b) => b - a);
    }, [records]);

    const currency = records[0]?.currency || "€";

    if (!open) return null;

    return (
        <div className="modal-backdrop financial-modal-backdrop" onClick={handleClose}>
            <div
                className="modal glass financial-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="financial-modal-title"
                aria-modal="true"
            >
                <div className="modal-header">
                    <div>
                        <h3 id="financial-modal-title">Apartment Financial Overview</h3>
                        <p className="financial-modal-subtitle">
                            Select an apartment to view all linked financial records.
                        </p>
                    </div>
                    <button className="icon-btn" onClick={handleClose} type="button" aria-label="Close">
                        ✕
                    </button>
                </div>

                <div className="modal-body financial-modal-body">
                    <SearchableApartmentSelect
                        apartments={apartmentList}
                        value={selectedApartmentId}
                        onChange={setSelectedApartmentId}
                        loading={apartmentsLoading}
                    />

                    {selectedApartmentId && !loading && !error && records.length > 0 && (
                        <div className="financial-filter-bar">
                            <span className="financial-filter-label">Filter by</span>
                            <select
                                value={yearFilter}
                                onChange={(e) => setYearFilter(e.target.value)}
                                aria-label="Filter by year"
                            >
                                <option value="">All years</option>
                                {availableYears.map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={monthFilter}
                                onChange={(e) => setMonthFilter(e.target.value)}
                                aria-label="Filter by month"
                            >
                                {MONTHS.map((m) => (
                                    <option key={m.value || "all"} value={m.value}>
                                        {m.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {selectedApartmentId && (
                        <>
                            <div className="financial-apartment-label">
                                Apartment: <strong>{apartmentNumber || "—"}</strong>
                            </div>

                            {error ? (
                                <div className="financial-error-state">
                                    <span className="financial-error-icon">⚠️</span>
                                    <p>{error}</p>
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => setRetryKey((k) => k + 1)}
                                    >
                                        Try again
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <FinancialSummaryCards summary={summary} loading={loading} />

                                    <FinancialTransactionsTable
                                        records={filteredRecords}
                                        loading={loading}
                                        currency={currency}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
